import { colors } from '@dexma/ui-components';
import i18n from 'asset/translation';

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

interface GeneratePDFOverlayBodyProps {
    progress: number;
    color: string;
    onDownload: () => void;
    location: string;
    period: string;
    completion: string;
    language: string;
    name: string;
}

const StyledProgressCard = styled.div`
    background-color: ${colors.white};
    color: ${colors.gray800};
    padding: 2rem 2.5rem;
    min-width: 800px;
    max-width: 1200px;
    text-align: left;
    align-self: center;
    margin-top: 10vh;
    font-size: 14px;
    font-weight: 400;
    .title {
        font-weight: 500;
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 4px;
    }
`;

const GeneratePDFOverlayBody: React.FC<GeneratePDFOverlayBodyProps> = ({
    progress,
    color,
    onDownload: _onDownload,
    period,
    language,
    location,
    completion,
    name,
}) => (
    <StyledProgressCard>
        <div className='title'>{i18n.t('reports.progressOverlay.title')}</div>
        <div>{i18n.t('reports.progressOverlay.message')}</div>
        <div>{i18n.t('reports.progressOverlay.dontClose')}</div>

        <ProgressBar color={color} percent={progress} completion={completion} />

        <StyledMetadataRow>
            {i18n.t('reports.progressOverlay.reportName')}: <StyledHighlight>{name}</StyledHighlight>
        </StyledMetadataRow>
        <StyledMetadataRow>
            {i18n.t('reports.progressOverlay.location')}: <StyledHighlight>{location}</StyledHighlight>
        </StyledMetadataRow>
        <StyledMetadataRow>
            {i18n.t('reports.progressOverlay.period')}: <StyledHighlight>{period}</StyledHighlight>
        </StyledMetadataRow>
        <StyledMetadataRow>
            {i18n.t('reports.progressOverlay.language')}: <StyledHighlight>{language}</StyledHighlight>
        </StyledMetadataRow>
    </StyledProgressCard>
);

type ProgressBarProps = {
    percent: number;
    color: string;
    completion: string;
};

export const ProgressBar = ({ percent, color, completion }: ProgressBarProps) => {
    const [displayedPercent, setDisplayedPercent] = useState(0);

    useEffect(() => {
        if (displayedPercent === 0) {
            const timeout = setTimeout(() => {
                setDisplayedPercent(percent);
            }, 20);
            return () => clearTimeout(timeout);
        }
        setDisplayedPercent(percent);
        return undefined;
    }, [percent, displayedPercent]);

    return (
        <>
            <BarWrapper>
                <BarFill $color={color} style={{ width: `${displayedPercent}%` }} />
            </BarWrapper>
            <CompletionTextWrapper>
                {i18n.t('reports.progressOverlay.completion')}: <StyledHighlight>{completion}</StyledHighlight>
            </CompletionTextWrapper>
        </>
    );
};

const StyledHighlight = styled.span`
    font-weight: 600;
`;

const StyledMetadataRow = styled.div`
    font-size: 12px;
`;

const CompletionTextWrapper = styled.div`
    margin: 6px 0 16px;
`;

const BarWrapper = styled.div`
    width: 100%;
    height: 20px;
    background-color: ${colors.gray100};
    overflow: hidden;
    margin-top: 16px;
    border-radius: 4px;
    border: 1px solid ${colors.gray300};
`;

const BarFill = styled.div<{ $color: string }>`
    height: 100%;
    background-color: ${({ $color }) => $color};
    transition: width 0.2s ease-in-out;
    will-change: width;
`;
export default GeneratePDFOverlayBody;
